import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Menu, Search, FileDown } from "lucide-react";
import { CATALOGUE_DOWNLOAD_URL } from "./CatalogueButtons";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { company, navLinks } from "../mock";

export const Logo = ({ light = false }) => (
  <Link to="/" className="flex items-center gap-3 group" aria-label={company.name}>
    <span className="logo-mark" aria-hidden="true">
      <span className="logo-mark__a">A</span>
      <span className="logo-mark__i">i</span>
      <span className="logo-mark__c">C</span>
    </span>
    <span className="leading-tight">
      <span className={`block font-bold text-[20px] md:text-[22px] tracking-tight ${light ? "text-white" : "text-[#231f20]"}`}>
        Anjaneya <span className="font-normal">Inks &amp; Coating</span>
      </span>
      <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7ab929] font-semibold">
        Colors That Speak
      </span>
    </span>
  </Link>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className={`site-header ${scrolled ? "site-header--scrolled" : ""}`} data-testid="site-header">
      {/* Top utility bar */}
      <div className="hidden md:block border-b border-[#eeeeee] bg-white">
        <div className="container-dic flex items-center justify-end h-9 text-[12.5px] text-[#4d4d4f]">
          <a href={`tel:${company.phone}`} className="flex items-center gap-1.5 hover:text-[#7ab929] transition-colors" data-testid="topbar-phone">
            <Phone size={13} /> Call Us: {company.phoneDisplay}
          </a>
          <span className="mx-2 text-[#bbb]">/</span>
          <a href={`tel:${company.phone2}`} className="hover:text-[#7ab929] transition-colors" data-testid="topbar-phone2">
            {company.phone2Display}
          </a>
          <span className="mx-4 h-3.5 w-px bg-[#d9d9d9]" />
          <a href={`mailto:${company.email}`} className="flex items-center gap-1.5 hover:text-[#7ab929] transition-colors" data-testid="topbar-email">
            <Mail size={13} /> {company.email}
          </a>
          <span className="mx-4 h-3.5 w-px bg-[#d9d9d9]" />
          <span className="flex items-center gap-1.5">
            <MapPin size={13} /> Medchal, Telangana
          </span>
          <span className="mx-4 h-3.5 w-px bg-[#d9d9d9]" />
          <a href={CATALOGUE_DOWNLOAD_URL} className="flex items-center gap-1.5 font-semibold text-[#7ab929] hover:text-[#5f9a17] transition-colors" data-testid="topbar-catalogue">
            <FileDown size={13} /> Catalogue PDF
          </a>
          <span className="mx-4 h-3.5 w-px bg-[#d9d9d9]" />
          <span className="font-bold text-[#231f20] text-[14px]">
            <span className="font-black">Anjaneya</span> Inks Ltd.
          </span>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-white">
        <div className="container-dic flex items-center justify-between h-[68px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
            {navLinks.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
              >
                {l.label}
              </NavLink>
            ))}
            <button type="button" className="ml-3 p-2 text-[#4d4d4f] hover:text-[#7ab929] transition-colors" aria-label="Search">
              <Search size={17} />
            </button>
            <Link to="/contact" className="btn-green ml-3" data-testid="nav-reach-us">
              Reach Us
            </Link>
          </nav>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button type="button" className="lg:hidden p-2 text-[#231f20]" aria-label="Open menu" data-testid="mobile-menu-button">
                <Menu size={26} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white p-0">
              <div className="p-6 border-b border-[#eee]">
                <Logo />
              </div>
              <nav className="flex flex-col p-4" aria-label="Mobile">
                {navLinks.map((l) => (
                  <NavLink
                    key={l.path}
                    to={l.path}
                    end={l.path === "/"}
                    className={({ isActive }) =>
                      `px-3 py-3 text-[16px] border-b border-[#f1f1f1] ${isActive ? "text-[#7ab929] font-bold" : "text-[#231f20]"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Link to="/contact" className="btn-green mt-5 justify-center">Reach Us</Link>
                <a href={CATALOGUE_DOWNLOAD_URL} className="btn-outline-dark mt-3 justify-center"><FileDown size={15} /> Catalogue PDF</a>
                <div className="mt-6 text-[13px] text-[#58595b] space-y-2">
                  <a href={`tel:${company.phone}`} className="flex items-center gap-2"><Phone size={14} /> {company.phoneDisplay}</a>
                  <a href={`tel:${company.phone2}`} className="flex items-center gap-2"><Phone size={14} /> {company.phone2Display}</a>
                  <a href={`mailto:${company.email}`} className="flex items-center gap-2"><Mail size={14} /> {company.email}</a>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
